import { Divider, Stack, Typography } from '@mui/material';
import { concat, get } from 'lodash';
import React, { FC } from 'react';
import CreateForm from '../../../../../../../components/ui-component/form-component/create-form/CreateForm';
import { useApplication } from '../../../../../../../context/ApplicationContext';
import { FORM_FIELDS_TYPES } from '../../../../../../../lib/constants/constant';
import { FormStrucItem } from '../../../../../../../lib/types/types';
import { NotFutureDateValidation, RadioButtonValidation, Required, ValidDate } from '../../../../../../../lib/validation/ValidationTypes';
import { mapCustomFormStrucIndex } from '../../../../../../../utils/formStruc';
import { checkReadOnlyInUeberarbeitung, isTrue } from '../../../../../../../utils/helper';

// import schemas
import Schema from '../../../../../../../lib/paths/antrag_FULL_Paths.json';
const P = Schema.attributes;

const BereitsErhalteneBeihilfenGlobal: FC<{ hasComment?: boolean }> = ({ hasComment }) => {
	const { application, setApplicationProperty, readOnly } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	return (
		<>
			<Typography variant='h3'>
				Angaben zu <u>bereits erhaltenen</u> weiteren De-minimis-Beihilfen
			</Typography>

			<Stack>
				<Typography>Im Folgenden geht es um De-minimis-Beihilfen im Sinne folgender Verordnungen:</Typography>
				<ul>
					<li>Allgemeine-De-minimis-Beihilfen</li>
					<li>Agrar-De-minimis-Beihilfen</li>
					<li>Fisch-De-minimis-Beihilfen</li>
				</ul>
				<Typography>Weitere Informationen und Details zu den Verordnungen finden Sie im Bereich "II. Definitionen und Erläuterungen".</Typography>
				<Divider sx={{ mt: '1rem' }} />
			</Stack>

			<CreateForm
				formStruc={formStrucMinimisErhaltenCheckbox()}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={updatedReadOnly}
			/>

			<CreateForm
				formStruc={formStrucMinimisErhaltenTabelle(application)}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={updatedReadOnly}
			/>
		</>
	);
};
export default BereitsErhalteneBeihilfenGlobal;

const formStrucMinimisErhaltenCheckbox = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Deminimis_Deminimis_Beihilfe_Erhalten,
					type: FORM_FIELDS_TYPES.radio,
					validation: [{ validateFunction: RadioButtonValidation }],
					label: 'Hiermit bestätige(n) ich/wir, dass das von mir/uns vertretene Unternehmen sowie im Sinne der Verordnung (EU) 2023/2831 mit ihm verbundene Unternehmen in einem Zeitraum von drei Jahren über die beantragte De-minimis-Beihilfe hinaus',
					options: [
						{ value: false, label: '<u>keine</u> De-minimis-Beihilfen im Sinne der o.g. Verordnungen erhalten haben.' },
						{
							value: true,
							label: 'die in der folgenden Tabelle angegebenen De-minimis-Beihilfen im Sinne folgender Verordnungen erhalten haben.',
						},
					],
					direction: 'column',
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
	return struc;
};

const formStrucMinimisErhaltenTabelle = (application: any) => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Deminimis_Bereits_Erhaltene_Beihilfen,
					type: FORM_FIELDS_TYPES.componentTable,
					colGroups: true,
					colGroupsProps: {
						startingKey: 'Allgemein',
						span: 3,
						label: 'De-minimis-Beihilfen',
						tooltip:
							'Bitte kreuzen Sie an, um welche De-minimis-Beihilfen es sich handelt und berücksichtigen Sie die unterschiedlichen Zeiträume, die zu betrachten sind (Allgemein: 36 Monate zurück bezogen auf den Zeitpunkt der Antragstellung; Agrar und Fisch: laufendes Jahr und zwei vorangegangene, vollständige Kalenderjahre).',
					},
					fieldProps: {
						Unternehmen: { tooltip: 'Unternehmen oder Unternehmensverbund gemäß Art. 2 Abs. 2 VO 2023/2831' },
						Datum: { width: '10.2rem' },
						Allgemein: { width: '40px', wirtingMode: 'sideways-lr' },
						Agrar: { width: '40px', wirtingMode: 'sideways-lr' },
						Fisch: { width: '40px', wirtingMode: 'sideways-lr' },
						Foerdersumme: { format: 'currency', tooltip: 'z.B. Zuschuss-, Darlehens-, Bürgschaftsbetrag', width: '10rem' },
						Beihilfewert: {
							format: 'currency',
							width: '10rem',
							tooltip: 'Die Information kann i.d.R. dem entsprechenden Bescheid oder der De-minimis-Bescheinigung entnommen werden.',
						},
					},
					tableProps: {
						radioGroups: ['Allgemein', 'Agrar', 'Fisch'], // neue Eigenschaft
					},
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];

	if (isTrue(get(application, P.Deminimis_Deminimis_Beihilfe_Erhalten.path))) return struc;
	else return [];
};
// custom formstruc for specific validation in componentTable
const customFormStrucMinimisErhaltenTabelle = (application: any) => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				P.Deminimis_Bereits_Erhaltene_Beihilfen.schema.attributes.Agrar,
				P.Deminimis_Bereits_Erhaltene_Beihilfen.schema.attributes.Allgemein,
				P.Deminimis_Bereits_Erhaltene_Beihilfen.schema.attributes.Beihilfegeber,
				P.Deminimis_Bereits_Erhaltene_Beihilfen.schema.attributes.Beihilfewert,
				{
					...P.Deminimis_Bereits_Erhaltene_Beihilfen.schema.attributes.Datum,
					validation: [{ validateFunction: Required }, { validateFunction: ValidDate }, { validateFunction: NotFutureDateValidation }],
				},
				P.Deminimis_Bereits_Erhaltene_Beihilfen.schema.attributes.Fisch,
				P.Deminimis_Bereits_Erhaltene_Beihilfen.schema.attributes.Foerdersumme,
				P.Deminimis_Bereits_Erhaltene_Beihilfen.schema.attributes.Kennzeichen,
				P.Deminimis_Bereits_Erhaltene_Beihilfen.schema.attributes.Unternehmen,
			],
		},
	];
	if (isTrue(get(application, P.Deminimis_Deminimis_Beihilfe_Erhalten.path))) return struc;
	else return [];
};

export const getFormStrucDeMinimisErhalteBeihilfen = (application: any) => {
	// const tabelleStruc = getFormStrucComponentTable(application, formStrucMinimisErhaltenTabelle(application));
	let tabelleStruc: FormStrucItem[] = mapCustomFormStrucIndex(
		application,
		customFormStrucMinimisErhaltenTabelle(application),
		P.Deminimis_Bereits_Erhaltene_Beihilfen,
	);

	return concat(formStrucMinimisErhaltenCheckbox(), tabelleStruc);
};
